import classnames from 'classnames';
import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { caseUtils } from '@codametrix/ui-common';

export type CaseStatusProps = {
  caseRun: CMxAPI.CaseRun;
  groupedCases?: CMxCommonApp.GroupedCases;
  billingGroupLoadState?: CMxCommonApp.Loading;
  timeStamp?: string;
  isPreGoLive?: boolean;
  includeBox?: boolean;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type StandaloneStatusProps = {
  caseRun: CMxAPI.CaseRun;
  secondaryPrefix?: string;
  isPreGoLive?: boolean;
  secondaryIndicatorVisibility?: boolean;
  includeBox?: boolean;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const DefaultGroupedCases: CMxCommonApp.GroupedCases = {
  groupType: 'none',
  caseRuns: []
};

const DEFAULT_PROPS = {
  groupedCases: { ...DefaultGroupedCases },
  secondaryPrefix: '',
  isPreGoLive: false,
  secondaryIndicatorVisibility: true
};

const getMiniStatus = (caseRun: CMxAPI.CaseRun) => {
  const statusDisplay = caseUtils.deriveStatus(caseRun);
  // TODO: CDS does not currently support exam_description
  // const description = caseRun.exam_description ?? '';
  const clinicalId = `${caseRun.primary_clinical_identifier.number} (${caseRun.primary_clinical_identifier.issuer})`;
  const caseType = caseRun.case_type ? `- ${caseRun.case_type}` : '';

  const styleClasses = [
    'related-group-item',
    'qa-related-group-item',
    statusDisplay.styleClass
  ];

  return (
    <li className={classnames(styleClasses)} key={caseRun.process_instance_id}>
      <a
        className="qa-related-group-item-link"
        href={`/cmx/cases/${
          caseRun.case_uid?.length > 0 ? caseRun.case_uid : caseRun.case_id
        }`}
        data-case-id={`${
          caseRun.case_uid?.length > 0 ? caseRun.case_uid : caseRun.case_id
        }`}
        title={`${caseRun.exam_description}`}>
        <div className="info-display">
          <div className="circle"></div>
          {statusDisplay.signifier}
        </div>
        <div className="descriptive-text ellipsify">{`${caseRun.exam_description}`}</div>
        <div className="clinical-id sensitive-data">#{clinicalId}</div>
      </a>
    </li>
  );
};

const getBillingGroup = (groupedCases: CMxCommonApp.GroupedCases) => {
  const { caseRuns } = groupedCases;

  if (!caseRuns || !caseRuns.length) {
    return false;
  }

  const relatedStatuses = caseRuns.map(getMiniStatus);

  return (
    <div className="relation-grouping">
      <span className="relation-grouping-reason">Billed with:</span>
      <ul className="related-group">{relatedStatuses}</ul>
    </div>
  );
};

const StatusDescription = (props: StandaloneStatusProps) => {
  const computed = { ...DEFAULT_PROPS, ...props };
  const {
    caseRun: caseDetail,
    secondaryPrefix,
    isPreGoLive: passedPreGoLive,
    groupedCases,
    secondaryIndicatorVisibility,
    includeBox,
    ...rest
  } = computed;

  const isPreGoLive = passedPreGoLive || caseUtils.isPreGoLive(caseDetail);
  const statusDisplay = caseUtils.deriveStatus(caseDetail);
  const statusSignifier = statusDisplay.signifier;

  var secondaryIndicatorText: boolean | string = caseUtils.getStatusReason(
    caseDetail
  );

  const classes = [
    props.className,
    'standalone-status',
    'description',
    statusDisplay.styleClass
  ];

  return (
    <div
      className={
        includeBox && secondaryIndicatorText ? 'status-description' : ''
      }>
      <span {...rest} className={classnames(classes)}>
        <span className="status-signifier">{statusSignifier}</span>

        {isPreGoLive && includeBox !== true && (
          <span className="status-modifier">Pre Go-Live</span>
        )}

        {secondaryIndicatorVisibility && secondaryIndicatorText && (
          <span className="secondary-indicator-text">
            <span className="secondary-indicator">
              {secondaryIndicatorText}
            </span>
          </span>
        )}
      </span>
    </div>
  );
};
StatusDescription.displayName = 'StatusDescription';

const CaseStatus: React.FC<CaseStatusProps> = (props: CaseStatusProps) => {
  const computed = { ...DEFAULT_PROPS, ...props };
  const {
    caseRun: caseDetail,
    timeStamp,
    isPreGoLive,
    groupedCases,
    secondaryPrefix,
    billingGroupLoadState,
    includeBox,
    ...rest
  } = computed;
  const statusDisplay = caseUtils.deriveStatus(caseDetail);

  const showLoading =
    billingGroupLoadState !== null && billingGroupLoadState?.initial;

  let readout = (
    <div className="large-info-display">
      <div className="col-code">
        <span className="large-circle" />
      </div>
      <div className="description-container">
        <StatusDescription
          caseRun={caseDetail}
          isPreGoLive={isPreGoLive}
          includeBox
        />

        <span className="description-timestamp">Updated {timeStamp} </span>
        {showLoading && (
          <div className="billing-group-loading-message relation-grouping">
            Checking for other billed exams...
          </div>
        )}
        {getBillingGroup(groupedCases)}
      </div>
    </div>
  );

  const classes = [props.className, 'case-status', statusDisplay.styleClass];

  return (
    <div {...rest} className={classnames(classes)}>
      {readout}
    </div>
  );
};
CaseStatus.displayName = 'CaseStatus';

export { CaseStatus, StatusDescription };
